import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DailyMetricChart from '../../components/analytics/DailyMetricChart';
import { Change, Section, StatTile } from '../../components/analytics/AnalyticsParts';
import { ChannelRow, FolderSection, hasRevenue } from '../../components/analytics/ChannelRows';
import {
  formatCompact,
  formatCompactMoney,
  formatDate,
  formatMoney,
  formatNumber,
} from '../../utils/analyticsFormat';
import { FolderFilter, RangeToolbar } from './AnalyticsToolbar';

const SORTS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'views', label: 'Views' },
  { value: 'rpm', label: 'RPM' },
];

function addDays(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// Adds up the shown channels' trend series into one chart series.
function combinedSeries(groups, summary, showRevenue) {
  const channels = groups.flatMap((group) => group.channels);
  const length = channels[0]?.series.views.length || 0;
  return Array.from({ length }, (_, index) => ({
    date: addDays(summary.range.from, index * summary.bucketDays),
    views: channels.reduce((sum, channel) => sum + channel.series.views[index], 0),
    ...(showRevenue && {
      revenue: channels.reduce(
        (sum, channel) => sum + channel.series.revenue[index] * channel.revenueShare,
        0,
      ),
    }),
  }));
}

function sortChannels(channels, sort) {
  const value = (channel) =>
    sort === 'revenue' ? (hasRevenue(channel) ? channel.revenueNet : null) : channel[sort];
  return [...channels].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (left === null || left === undefined) return 1;
    if (right === null || right === undefined) return -1;
    return right - left;
  });
}

function AttentionAlert({ groups }) {
  const channels = groups.flatMap((group) => group.channels).filter((channel) => channel.problem);
  if (!channels.length) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {channels.length === 1
          ? '1 channel needs attention:'
          : `${channels.length} channels need attention:`}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {channels.map((channel) => (
          <li key={channel.id}>
            <Typography variant="body2">
              <strong>{channel.name}</strong>: {channel.problem}{' '}
              <Link component={RouterLink} to="/analytics/manage">
                Manage channels
              </Link>
            </Typography>
          </li>
        ))}
      </Box>
    </Alert>
  );
}

function SortControl({ sort, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Sort by
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={sort}
        onChange={(_, value) => value && onChange(value)}
        aria-label="Sort channels by"
        sx={{ bgcolor: 'background.paper' }}
      >
        {SORTS.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ px: 1.5 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// A folder's revenue (and its expenses and profit, once it has expenses) for
// partners; its views for everyone else.
function GroupSubtotal({ group, showRevenue }) {
  if (!showRevenue) {
    return (
      <>
        <Typography variant="body2" color="text.secondary">
          Views
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{formatNumber(group.views)}</Typography>
        <Change compact current={group.views} previous={group.previous.views} />
      </>
    );
  }
  const cut = group.revenueNet !== group.revenue;
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {cut ? 'Your revenue' : 'Revenue'}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
        {formatMoney(cut ? group.revenueNet : group.revenue)}
      </Typography>
      <Change
        compact
        current={cut ? group.revenueNet : group.revenue}
        previous={cut ? group.previous.revenueNet : group.previous.revenue}
      />
      {cut && (
        <Typography variant="body2" color="text.secondary">
          Total {formatMoney(group.revenue)}
        </Typography>
      )}
      {group.expenses > 0 && (
        <Typography variant="body2" color="text.secondary">
          · Expenses {formatMoney(group.expenses)} · Profit {formatMoney(group.profit)}
        </Typography>
      )}
    </>
  );
}

// Expenses not for any one channel are part of every total. Expenses count
// through today, without the days left out of revenue.
function ExpensesFootnote({ totals, range }) {
  return (
    <>
      {`Through ${formatDate(range.expenses.to)} · `}
      {totals.generalExpenses > 0 && `Includes ${formatMoney(totals.generalExpenses)} general · `}
      <Link component={RouterLink} to="/expenses">
        {totals.expenses > 0 ? 'View expenses' : 'Add expenses'}
      </Link>
    </>
  );
}

function EmptyState({ showRevenue }) {
  return (
    <Section>
      <Box sx={{ textAlign: 'center', py: 4, maxWidth: 520, mx: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Add your first channel
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {showRevenue
            ? 'Connect a YouTube channel through its proxy to pull its analytics and revenue every day, or add a channel you reach through a CMS from a YouTube Studio export.'
            : 'Add the channels you upload to, or connect a YouTube channel through its proxy, to pull its views every day.'}
        </Typography>
        <Button variant="contained" component={RouterLink} to="/analytics/manage">
          Add a channel
        </Button>
      </Box>
    </Section>
  );
}

export default function OverviewTab(props) {
  const { summary, loading, visibleGroups, totals, reload, showRevenue } = props;
  const [sort, setSort] = useState(showRevenue ? 'revenue' : 'views');
  const anyRevenue = visibleGroups.some((group) => group.channels.some(hasRevenue));
  const [chartMetric, setChartMetric] = useState('revenue');
  const metric = anyRevenue ? chartMetric : 'views';
  const series = useMemo(
    () => combinedSeries(visibleGroups, summary, showRevenue),
    [visibleGroups, summary, showRevenue],
  );
  const seriesDates = useMemo(() => series.map((point) => point.date), [series]);
  const cut = totals.revenueNet !== totals.revenue;
  const folders = summary.folders;
  const hasChannels = summary.groups.some((group) => group.channelCount > 0);
  const hasEstimates = visibleGroups.some((group) =>
    group.channels.some((channel) => ['estimated', 'estimated-rpm'].includes(channel.revenueNote)),
  );

  return (
    <>
      <RangeToolbar {...props} />
      {!hasChannels ? (
        <EmptyState showRevenue={showRevenue} />
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <FolderFilter {...props} />
          <AttentionAlert groups={visibleGroups} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: `repeat(${showRevenue ? 3 : 2}, minmax(0, 1fr))`,
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {showRevenue && (
              <StatTile
                label={cut ? 'Your revenue' : 'Revenue'}
                value={formatMoney(cut ? totals.revenueNet : totals.revenue)}
                change={
                  <Change
                    current={cut ? totals.revenueNet : totals.revenue}
                    previous={cut ? totals.previous.revenueNet : totals.previous.revenue}
                  />
                }
                footnote={
                  [cut && `Total ${formatMoney(totals.revenue)}`, hasEstimates && 'Includes estimated revenue']
                    .filter(Boolean)
                    .join(' · ') || null
                }
              />
            )}
            {showRevenue && (
              <StatTile
                label="Expenses"
                value={formatMoney(totals.expenses)}
                change={
                  <Change
                    current={totals.expenses}
                    previous={totals.previous.expenses}
                    formatDifference={formatMoney}
                    lowerIsBetter
                  />
                }
                footnote={<ExpensesFootnote totals={totals} range={summary.range} />}
              />
            )}
            {showRevenue && (
              <StatTile
                label="Profit"
                value={formatMoney(totals.profit)}
                change={
                  <Change
                    current={totals.profit}
                    previous={totals.previous.profit}
                    formatDifference={formatMoney}
                  />
                }
                footnote={cut ? 'Your revenue minus expenses' : 'Revenue minus expenses'}
              />
            )}
            <StatTile
              label="Views"
              value={formatCompact(totals.views)}
              change={<Change current={totals.views} previous={totals.previous.views} />}
              footnote={formatNumber(totals.views)}
            />
            {showRevenue && (
              <StatTile
                label="RPM"
                value={formatMoney(totals.rpm)}
                change={<Change current={totals.rpm} previous={totals.previous.rpm} />}
                footnote="Total revenue per 1,000 views"
              />
            )}
            <StatTile
              label="Channels"
              value={totals.channelCount}
              footnote={
                totals.needsAttention
                  ? `${totals.needsAttention} need${totals.needsAttention === 1 ? 's' : ''} attention`
                  : 'All up to date'
              }
            />
          </Box>

          <Section
            title={metric === 'revenue' ? (cut ? 'Your revenue' : 'Revenue') : 'Views'}
            subtitle={
              summary.bucketDays > 1
                ? `Each point covers ${summary.bucketDays} days.`
                : 'Per day, for the folders shown.'
            }
            action={
              anyRevenue && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={metric}
                  onChange={(_, value) => value && setChartMetric(value)}
                  aria-label="Chart metric"
                >
                  <ToggleButton value="revenue">Revenue</ToggleButton>
                  <ToggleButton value="views">Views</ToggleButton>
                </ToggleButtonGroup>
              )
            }
          >
            <DailyMetricChart
              data={series}
              bucketDays={summary.bucketDays}
              value={(day) => day[metric]}
              format={metric === 'revenue' ? formatCompactMoney : formatCompact}
              label={metric === 'revenue' ? 'Revenue' : 'Views'}
            />
          </Section>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
              Channels
            </Typography>
            {showRevenue && <SortControl sort={sort} onChange={setSort} />}
          </Box>

          {visibleGroups.map((group) => (
            <FolderSection
              key={group.id || 'ungrouped'}
              group={group}
              subtotal={<GroupSubtotal group={group} showRevenue={showRevenue} />}
            >
              {sortChannels(group.channels, sort).map((channel) => (
                <ChannelRow
                  key={channel.id}
                  channel={channel}
                  showRevenue={showRevenue}
                  seriesDates={seriesDates}
                  bucketDays={summary.bucketDays}
                  folders={folders}
                  existingTags={summary.tags}
                  onChanged={reload}
                />
              ))}
            </FolderSection>
          ))}
          {!visibleGroups.length && (
            <Alert severity="info">All folders are hidden. Choose a folder above to show it.</Alert>
          )}
        </Box>
      )}
    </>
  );
}
